/**
 * Report tool — "Feature Report" template.
 *
 * The first of what will become several report templates (per the admin's
 * own framing) — pick a layer, pick features (selected or all), pick which
 * attribute fields to show, and get one document with a small map
 * thumbnail (current basemap + the layer's own symbol + its label, if
 * labeling is on) next to the chosen attributes, one block per feature.
 *
 * Reuses the exact pieces the rest of the app already has for each part:
 * `ctx.getSelection()` for "from selected" (`SelectByLocationPanel`'s own
 * pattern), `loadFeaturesDetailed` for "all", `fetchLayerFieldNames` for
 * the field checklist, `captureFeatureThumbnail` (new, built for this
 * tool) for the per-feature image, and `printSheet.ts`'s existing
 * download/print actions — the same hand-drawn-canvas, no-PDF-library
 * convention the Print tool already established.
 */

import { useEffect, useState } from 'react';
import { Loader2, FileText } from 'lucide-react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { scopedLayers, type ToolContext } from './types';
import { EmptyHint, Field, LayerSelect, Notice, btnClass, inputClass } from './shared';
import { fetchLayerFieldNames } from '../../../tools/toolFields';
import { loadFeaturesDetailed, centroidOf } from './featureLoad';
import { captureFeatureThumbnail } from '../../../utils/featureThumbnail';
import { renderReportSheet, type ReportBlock } from '../../../utils/reportSheet';
import { printSheet, downloadSheet, type Sheet } from '../../../utils/printSheet';

const THUMB_W = 240;
const THUMB_H = 160;
const MAX_FEATURES = 5000;

type SourceMode = 'selected' | 'all';

export function ReportPanel({ ctx }: { ctx: ToolContext }) {
  const allowed = scopedLayers(ctx.config, ctx.layers);

  const [layerId, setLayerId] = useState(allowed[0]?.id ?? '');
  const [mode, setMode] = useState<SourceMode>('selected');
  const [fields, setFields] = useState<string[]>([]);
  const [fieldsBusy, setFieldsBusy] = useState(false);
  const [checkedFields, setCheckedFields] = useState<Set<string>>(new Set());
  const [includeXY, setIncludeXY] = useState(true);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('Feature Report');

  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [sheet, setSheet] = useState<Sheet | null>(null);

  const source = allowed.find((layer) => layer.id === layerId);

  // Fields for the chosen layer — fetched live, the same race-guarded
  // pattern SearchPanel.tsx already uses, since the admin picks the layer
  // inside this tool rather than at Tool Library config time. Keyed on
  // `layerId` alone, not the `source` object itself: `ctx.layers` gets a
  // new array/object identity on most app renders for reasons unrelated
  // to this tool, and keying on the object would re-run this (and wipe
  // out a custom-typed title) on every one of them.
  useEffect(() => {
    setFields([]);
    setCheckedFields(new Set());
    setSheet(null);
    const layer = allowed.find((entry) => entry.id === layerId);
    if (!layer) return;
    setTitle(`${layer.displayName ?? layer.name} Report`);
    let live = true;
    setFieldsBusy(true);
    void fetchLayerFieldNames(layer, ctx.module).then((names) => {
      if (!live) return;
      setFields(names.sort());
      setFieldsBusy(false);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerId]);

  const toggleField = (name: string) =>
    setCheckedFields((current) => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const generate = async () => {
    if (!source) return;
    setError(null);
    setSheet(null);
    setTruncated(false);
    setBusy(true);
    setProgress(null);
    try {
      let records: { properties: Record<string, unknown>; geometry: GeoJSON.Geometry | null }[];

      if (mode === 'selected') {
        const rows = (ctx.getSelection?.() ?? []).filter(
          (row) => row.layerId === source.id && row.geometry,
        );
        if (rows.length === 0) {
          setError('No features are currently selected on this layer.');
          return;
        }
        records = rows.map((row) => ({
          properties: (row.properties ?? {}) as Record<string, unknown>,
          geometry: (row.geometry ?? null) as GeoJSON.Geometry | null,
        }));
      } else {
        const { features, truncated: wasTruncated } = await loadFeaturesDetailed(source, ctx, {
          limit: MAX_FEATURES,
        });
        if (features.length === 0) {
          setError('That layer returned no features.');
          return;
        }
        setTruncated(wasTruncated);
        records = features.map((feature) => ({
          properties: (feature.properties ?? {}) as Record<string, unknown>,
          geometry: feature.geometry ?? null,
        }));
      }

      const blocks: ReportBlock[] = [];
      setProgress({ done: 0, total: records.length });

      // Sequential, not Promise.all — each thumbnail is a real small map
      // waterfall-loading real tiles; one at a time keeps memory/network
      // load predictable regardless of how many features this report covers.
      for (let index = 0; index < records.length; index += 1) {
        const record = records[index];
        const rows: { label: string; value: string }[] = [];
        for (const field of fields) {
          if (!checkedFields.has(field)) continue;
          const value = record.properties[field];
          rows.push({ label: field, value: value === null || value === undefined ? '' : String(value) });
        }
        if (includeXY) {
          const centre = centroidOf(record.geometry);
          if (centre) {
            const [lng, lat] = centre;
            rows.push({ label: 'XY', value: `${lat.toFixed(5)}, ${lng.toFixed(5)}` });
          }
        }

        const feature: GeoJSON.Feature = {
          type: 'Feature',
          geometry: record.geometry as GeoJSON.Geometry,
          properties: record.properties,
        };
        const thumbnail = record.geometry
          ? await captureFeatureThumbnail(feature, source, ctx.basemap, THUMB_W, THUMB_H)
          : null;

        blocks.push({ thumbnail, rows });
        setProgress({ done: index + 1, total: records.length });
      }

      setSheet(renderReportSheet(title, subtitle, blocks));
    } catch {
      setError('Could not generate the report — try again.');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <>
      <ToolPanelHeader
        title="Report"
        description="A feature-by-feature report: a map thumbnail next to the attributes you choose."
        onClose={ctx.onClose}
      />

      <Field label="Layer">
        <LayerSelect layers={allowed} value={layerId} onChange={setLayerId} />
      </Field>

      <div className="flex gap-1 rounded-md bg-slate-100 p-0.5 text-[11px] font-medium">
        <button
          type="button"
          onClick={() => setMode('selected')}
          className={`flex-1 rounded px-2 py-1 transition ${
            mode === 'selected' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          From selected features
        </button>
        <button
          type="button"
          onClick={() => setMode('all')}
          className={`flex-1 rounded px-2 py-1 transition ${
            mode === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          All features
        </button>
      </div>

      <Field label="Title">
        <input value={title} onChange={(event) => setTitle(event.target.value)} className={inputClass} />
      </Field>

      <Field label="Subtitle">
        <input value={subtitle} onChange={(event) => setSubtitle(event.target.value)} className={inputClass} />
      </Field>

      <Field label="Fields to include">
        <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
          <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-white">
            <input
              type="checkbox"
              checked={includeXY}
              onChange={(event) => setIncludeXY(event.target.checked)}
              className="h-3.5 w-3.5 accent-blue-600"
            />
            <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-700">Coordinates (XY)</span>
          </label>
          {fieldsBusy && <p className="px-1 py-1 text-[11px] text-slate-400">Loading fields…</p>}
          {!fieldsBusy &&
            fields.map((name) => (
              <label key={name} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-white">
                <input
                  type="checkbox"
                  checked={checkedFields.has(name)}
                  onChange={() => toggleField(name)}
                  className="h-3.5 w-3.5 accent-blue-600"
                />
                <span className="min-w-0 flex-1 truncate text-xs text-slate-700">{name}</span>
              </label>
            ))}
          {!fieldsBusy && fields.length === 0 && (
            <p className="px-1 py-1 text-[11px] text-slate-400">No fields found on this layer.</p>
          )}
        </div>
      </Field>

      <ToolActions>
        <button
          type="button"
          onClick={() => void generate()}
          disabled={!source || busy || (checkedFields.size === 0 && !includeXY)}
          className={`${btnClass} gl-btn--primary`}
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {busy ? 'Generating…' : 'Generate'}
        </button>
      </ToolActions>

      {progress && (
        <Notice>
          Rendering {progress.done} / {progress.total}…
        </Notice>
      )}
      {error && <Notice tone="warn">{error}</Notice>}
      {truncated && (
        <Notice tone="warn">
          Only the first {MAX_FEATURES.toLocaleString()} features were used — the layer (or its
          service) has more than that.
        </Notice>
      )}
      {!busy && !sheet && !error && <EmptyHint>Pick a layer and fields, then Generate.</EmptyHint>}

      {sheet && (
        <ToolActions>
          <button
            type="button"
            onClick={() => downloadSheet(sheet, title || 'report')}
            className={`${btnClass} gl-btn--secondary`}
          >
            <FileText className="h-3.5 w-3.5" />
            Download PNG
          </button>
          <button
            type="button"
            onClick={() => printSheet(sheet, title || 'Report')}
            className={`${btnClass} gl-btn--secondary`}
          >
            Print / Save as PDF
          </button>
        </ToolActions>
      )}
    </>
  );
}
